import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  Building2, 
  MapPin, 
  Calendar, 
  Edit, 
  Trash2, 
  TrendingUp,
  DollarSign,
  Percent,
  ArrowLeft,
  Plus,
  Receipt
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  getProperty, 
  deleteProperty, 
  getExpenses,
  createExpense,
  deleteExpense,
  getCashflow,
  formatCurrency, 
  formatPercent,
  EXPENSE_CATEGORIES,
  FREQUENCIES
} from "@/lib/api";

const MetricBox = ({ label, value, subValue, icon: Icon, variant = "default" }) => (
  <div className="p-4 rounded-lg bg-slate-50 border border-slate-100">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-slate-400" />
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
    </div>
    <p className={`text-xl font-bold ${
      variant === "success" ? "text-emerald-600" : 
      variant === "warning" ? "text-amber-600" : 
      "text-slate-900"
    }`}>
      {value}
    </p>
    {subValue && <p className="text-sm text-slate-500 mt-1">{subValue}</p>}
  </div>
);

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [cashflow, setCashflow] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [newExpense, setNewExpense] = useState({
    category: "",
    amount: "",
    frequency: "monthly",
    is_percentage: false,
    description: ""
  });

  const fetchData = async () => {
    try {
      const [propertyRes, expensesRes, cashflowRes] = await Promise.all([
        getProperty(id),
        getExpenses(id),
        getCashflow(id)
      ]);
      setProperty(propertyRes.data);
      setExpenses(expensesRes.data);
      setCashflow(cashflowRes.data);
    } catch (error) {
      console.error("Error fetching property:", error);
      toast.error("Failed to load property details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleDelete = async () => {
    try {
      await deleteProperty(id);
      toast.success("Property deleted successfully");
      navigate("/properties");
    } catch (error) {
      console.error("Error deleting property:", error);
      toast.error("Failed to delete property");
    }
  };

  const handleAddExpense = async () => {
    if (!newExpense.category || !newExpense.amount) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await createExpense({
        property_id: id,
        category: newExpense.category,
        amount: parseFloat(newExpense.amount),
        frequency: newExpense.is_percentage ? "annually" : newExpense.frequency,
        is_percentage: newExpense.is_percentage,
        description: newExpense.description
      });
      toast.success("Expense added successfully");
      setShowExpenseDialog(false);
      setNewExpense({ category: "", amount: "", frequency: "monthly", is_percentage: false, description: "" });
      fetchData();
    } catch (error) {
      console.error("Error adding expense:", error);
      toast.error("Failed to add expense");
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await deleteExpense(expenseId);
      setExpenses(expenses.filter(e => e.id !== expenseId));
      toast.success("Expense deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting expense:", error);
      toast.error("Failed to delete expense");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64 lg:col-span-2" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Property not found</p>
        <Link to="/properties">
          <Button className="mt-4">Back to Properties</Button>
        </Link>
      </div>
    );
  }

  const equity = property.current_value - property.loan_amount;
  const lvr = property.current_value > 0 
    ? (property.loan_amount / property.current_value) * 100 
    : 0;
  const capitalGrowth = property.purchase_price > 0 
    ? ((property.current_value - property.purchase_price) / property.purchase_price) * 100 
    : 0;
  const rentalYield = property.current_value > 0 
    ? ((property.rental_income_weekly * 52) / property.current_value) * 100 
    : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="property-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/properties">
            <Button variant="ghost" size="icon" className="rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">
              {property.address}
            </h2>
            <p className="text-slate-500 flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {property.suburb}, {property.state} {property.postcode}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/properties/${id}/edit`}>
            <Button variant="outline" className="rounded-full" data-testid="edit-property-btn">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </Link>
          <Button 
            variant="outline" 
            className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => setShowDeleteDialog(true)}
            data-testid="delete-property-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Property Info */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading">Property Details</CardTitle>
              <Badge variant="outline" className="capitalize">
                {property.property_type}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricBox 
                label="Current Value" 
                value={formatCurrency(property.current_value)}
                icon={Building2}
              />
              <MetricBox 
                label="Equity" 
                value={formatCurrency(equity)}
                variant="success"
                icon={TrendingUp}
              />
              <MetricBox 
                label="Loan Balance" 
                value={formatCurrency(property.loan_amount)}
                icon={DollarSign}
              />
              <MetricBox 
                label="LVR Ratio" 
                value={formatPercent(lvr)}
                variant={lvr > 80 ? "warning" : "default"}
                icon={Percent}
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricBox 
                label="Purchase Price" 
                value={formatCurrency(property.purchase_price)}
                icon={DollarSign}
              />
              <MetricBox 
                label="Capital Growth" 
                value={formatPercent(capitalGrowth)}
                variant={capitalGrowth > 0 ? "success" : "warning"}
                icon={TrendingUp}
              />
              <MetricBox 
                label="Weekly Rent" 
                value={formatCurrency(property.rental_income_weekly)}
                icon={Receipt}
              />
              <MetricBox 
                label="Rental Yield" 
                value={formatPercent(rentalYield)}
                icon={Percent}
              />
            </div>

            <div className="pt-4 border-t border-slate-100">
              <h4 className="font-semibold text-slate-700 mb-3">Loan Details</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Interest Rate:</span>
                  <span className="ml-2 font-medium">{property.interest_rate}%</span>
                </div>
                <div>
                  <span className="text-slate-500">Loan Term:</span>
                  <span className="ml-2 font-medium">{property.loan_term_years} years</span>
                </div>
                <div>
                  <span className="text-slate-500">Purchase Date:</span>
                  <span className="ml-2 font-medium">
                    {new Date(property.purchase_date).toLocaleDateString("en-AU")}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cashflow Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Cashflow Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {cashflow ? (
              <>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Annual Rental</span>
                    <span className="font-medium text-emerald-600">
                      +{formatCurrency(cashflow.annual_rental_income)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Annual Expenses</span>
                    <span className="font-medium text-red-500">
                      -{formatCurrency(cashflow.annual_expenses)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Annual Interest</span>
                    <span className="font-medium text-red-500">
                      -{formatCurrency(cashflow.annual_interest)}
                    </span>
                  </div>
                  <div className="h-px bg-slate-200" />
                  <div className="flex justify-between">
                    <span className="font-medium text-slate-700">Net Cashflow</span>
                    <span className={`font-bold ${
                      cashflow.net_cashflow >= 0 ? "text-emerald-600" : "text-red-500"
                    }`}>
                      {formatCurrency(cashflow.net_cashflow)}
                    </span>
                  </div>
                </div>

                {cashflow.negative_gearing_benefit > 0 && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">Negative Gearing Benefit:</span>
                      <br />
                      ~{formatCurrency(cashflow.negative_gearing_benefit)} tax offset
                    </p>
                  </div>
                )}
              </>
            ) : (
              <Skeleton className="h-48" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Expenses Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading">Expenses</CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            className="rounded-full"
            onClick={() => setShowExpenseDialog(true)}
            data-testid="add-expense-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Expense
          </Button>
        </CardHeader>
        <CardContent>
          {expenses.length > 0 ? (
            <div className="space-y-2">
              {expenses.map((expense) => (
                <div 
                  key={expense.id} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  data-testid={`expense-${expense.id}`}
                >
                  <div>
                    <p className="font-medium text-slate-700 capitalize">
                      {EXPENSE_CATEGORIES.find(c => c.value === expense.category)?.label || expense.category}
                    </p>
                    <p className="text-sm text-slate-500">
                      {expense.is_percentage 
                        ? `${expense.amount}% of rental income`
                        : `${formatCurrency(expense.amount)} / ${expense.frequency}`
                      }
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-slate-400 hover:text-red-500"
                    onClick={() => handleDeleteExpense(expense.id)}
                    data-testid={`delete-expense-${expense.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-6">
              No expenses recorded. Add your property expenses to track cashflow accurately.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Property</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{property.address}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Expense Dialog */}
      <Dialog open={showExpenseDialog} onOpenChange={(open) => {
        setShowExpenseDialog(open);
        // Reset form when dialog closes
        if (!open) {
          setNewExpense({ category: "", amount: "", frequency: "monthly", is_percentage: false, description: "" });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Expense</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select 
                value={newExpense.category} 
                onValueChange={(value) => {
                  // Only management_fees can use percentage - all others must be dollar amounts
                  const isManagement = value === "management_fees";
                  setNewExpense({
                    ...newExpense, 
                    category: value,
                    // Reset to false for non-management categories, default to true for management
                    is_percentage: isManagement ? true : false
                  });
                }}
              >
                <SelectTrigger data-testid="expense-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {newExpense.category === "management_fees" && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label htmlFor="is-percentage" className="font-medium">Use Percentage</Label>
                  <p className="text-sm text-slate-500">Calculate as % of rental income</p>
                </div>
                <Switch
                  id="is-percentage"
                  checked={newExpense.is_percentage}
                  onCheckedChange={(checked) => setNewExpense({...newExpense, is_percentage: checked})}
                  data-testid="expense-percentage-switch"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>{newExpense.is_percentage ? "Percentage *" : "Amount *"}</Label>
              <div className="relative">
                {newExpense.is_percentage ? (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                ) : (
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                )}
                <Input
                  type="number"
                  className={newExpense.is_percentage ? "pr-8" : "pl-7"}
                  placeholder={newExpense.is_percentage ? "8.8" : "0.00"}
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
                  data-testid="expense-amount-input"
                />
              </div>
              {newExpense.is_percentage && (
                <p className="text-xs text-slate-500">
                  Typical property management fees are 7-10% of rental income
                </p>
              )}
            </div>
            
            {!newExpense.is_percentage && (
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select 
                  value={newExpense.frequency} 
                  onValueChange={(value) => setNewExpense({...newExpense, frequency: value})}
                >
                  <SelectTrigger data-testid="expense-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((freq) => (
                      <SelectItem key={freq.value} value={freq.value}>
                        {freq.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                placeholder="Add a note..."
                value={newExpense.description}
                onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                data-testid="expense-description-input"
              />
            </div>
            <Button 
              className="w-full bg-[#547d91] hover:bg-[#456a7a] rounded-full"
              onClick={handleAddExpense}
              data-testid="save-expense-btn"
            >
              Add Expense
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
